import { Component, computed, input, OnChanges, output, SimpleChanges } from "@angular/core";
import { IRouteStopRow } from "src/app/models";
import { FeatureType, RouteEditMode, RoutePathType } from "src/app/models/enums";
import { EventsOptions, LiteralService, UtilsService } from "src/app/services";
import { TooltipPosition } from "src/app/shared/tooltip/tooltip-position";
import { images } from "src/images";
import { APP } from "src/app/config/app";


@Component({
  selector: 'app-route-design-modal',
  templateUrl: './route-design-modal.component.html',
  styleUrl: './route-design-modal.component.scss',
  standalone: false
})
export class RouteDesignModalComponent implements OnChanges {

  readonly type = input<string>();
  readonly list = input<any>();
  readonly selectedItems = input<any>([]);
  readonly creationSegments = input<boolean>(false);
  readonly mode = input<RouteEditMode>(RouteEditMode.DESIGN);
  readonly showOrderedStops = input<boolean>(false);
  readonly orderedStopsTownStops = input<any[]>([]);
  readonly orderedStopsTownCenter = input<any>(undefined);
  readonly orderedStopsApplying = input<boolean>(false);
  readonly orderedStopsInitialRows = input<any[]>([]);
  readonly orderedStopsSelectedStopIds = input<number[]>([]);
  readonly orderedStopsPathType = input<RoutePathType>(RoutePathType.LONGEST);
  readonly allowReorderStops = input<boolean>(false);
  readonly hasMissingSegments = computed(() => this.orderedStopsInitialRows().some((row: IRouteStopRow) => row.breakRow));
  onChangeSelectionType = output<any>();
  onClickSelectedItem = output<any>();
  onEditSelectedItem = output<any>();
  orderedStopsConfirm = output<any>();
  orderedStopsSelect = output<number | null>();
  orderedStopsPathTypeChange = output<RoutePathType>();

  public images = images;
  public TooltipPosition = TooltipPosition;
  public FeatureType = FeatureType;
  public RouteEditMode = RouteEditMode;
  public APP = APP;
  public RoutePathType = RoutePathType;

  filteredList: any[] = [];
  openedItem: any = undefined;
  searchValue = '';

  constructor(
    public literalService: LiteralService,
    public utilsService: UtilsService,
    public eventsOptions: EventsOptions
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['type']) this.searchValue = '';
    if (changes['list']) this.applySearch(changes['list'].currentValue);
    if (changes['selectedItems']) {
      const selected = changes['selectedItems'].currentValue || [];
      const currentList = changes['list']?.currentValue ?? this.list();
      if (selected.length > 0) {
        setTimeout(() => {
          this.onItemClick({
            event: null,
            items: currentList.filter((i: any) => selected.some((selectedItem: any) => selectedItem.getProperty('id') === i.id)),
          }, true);
        });
      } else {
        this.openedItem = undefined;
        this.applySearch(currentList);
      }
    }
  }

  isItemSelected(id: number) {
    return this.selectedItems().some((item: any) => item.getProperty('id') === id);
  }

  isItemEditable(id: number) {
    return this.eventsOptions.isEditableStop(id);
  }

  isSharedFieldEditable(id: number) {
    return this.isItemEditable(id) && this.eventsOptions.canEditSharedStopFields(id);
  }

  isTownStop(id: number) {
    return this.eventsOptions.isTownStop(id);
  }

  selectOrderedStopsPathType(pathType: RoutePathType) {
    if (pathType !== this.orderedStopsPathType()) this.orderedStopsPathTypeChange.emit(pathType);
  }

  onChangeSelectedType(type: string) {
    if (this.type() === type) {
      this.onChangeSelectionType.emit('');
    } else {
      this.onChangeSelectionType.emit(type);
    }
  }

  onItemClick(clickEvent: any, skipEmit?: boolean) {
    const { event, items } = clickEvent;
    let selectedIds = this.selectedItems().map((i: any) => i.getProperty('id'));
    if (event && event.shiftKey) {
      const clickedId = items[0].id;
      if (selectedIds.includes(clickedId)) {
        selectedIds = selectedIds.filter((id: any) => id !== clickedId);
      } else {
        selectedIds = [...new Set([...selectedIds, ...items.map((i: any) => i.id)])];
      }
    };
    if (!this.creationSegments() && ((event && !event.shiftKey) || (!event && selectedIds.length === 1))) {
      setTimeout(() => {
        const itemId = `${this.type()}-${items[0].id}`;
        const itemDetailsElement = document.getElementById(`${itemId}`);
        const focusNameInput = () => {
          const nameInput = document.getElementById(`${itemId}-name-input`);
          nameInput?.focus();
        };
        if (itemDetailsElement) {
          itemDetailsElement.scrollIntoView({ block: 'start', behavior: 'smooth' });
          setTimeout(focusNameInput, 200);
        }
      }, 250);
      this.openedItem = items[0];
      if (!skipEmit) this.onClickSelectedItem.emit({ids: [items[0].id], multiple: false});
    } else {
      this.openedItem = undefined;
      this.applySearch(this.list(), selectedIds);
      const itemsContainer = document.getElementById('feature-list-container');
      if (itemsContainer) {
        itemsContainer.scrollTop = 0;
      }
      if (!skipEmit) this.onClickSelectedItem.emit({ids: selectedIds, multiple: true});
    }
  }

  search(searchValue: string) {
    this.searchValue = searchValue;
    this.applySearch(this.list());
  }

  private applySearch(list: any[], selectedIds?: any[]) {
    const ids = selectedIds ?? this.selectedItems().map((item: any) => item.getProperty('id'));
    const filtered = this.filterByName(list);
    this.filteredList = ids.length > 1 ? this.selectedFirst(filtered, ids) : filtered;
  }

  private filterByName(list: any[]) {
    return (list ?? []).filter((item: any) => item.name.toLowerCase().includes(this.searchValue.toLowerCase()));
  }

  private selectedFirst(list: any[], selectedIds: any[]) {
    const selected = list.filter((item: any) => selectedIds.includes(item.id));
    const rest = list.filter((item: any) => !selectedIds.includes(item.id));
    return [...selected, ...rest];
  }

  // Structural: allows anything a valid value could be a prefix of, while typing.
  private readonly partialCoordinateRegExp: Record<'lat' | 'lng', RegExp> = {
    lat: /^-?\d{0,2}(\.\d{0,6})?$/,
    lng: /^-?\d{0,3}(\.\d{0,6})?$/,
  };

  // Strict: enforces the real geographic range (±90 for latitude, ±180 for longitude) with exactly 6 decimals.
  private readonly coordinateRegExp: Record<'lat' | 'lng', RegExp> = {
    lat: /^-?(90\.0{6}|[0-8]?\d\.\d{6})$/,
    lng: /^-?(180\.0{6}|(1[0-7]\d|[1-9]?\d)\.\d{6})$/,
  };

  blockInvalidCoordinateKeys(event: KeyboardEvent, valueToChange: 'lat' | 'lng') {
    const input = event.target as HTMLInputElement;
    if (event.metaKey || event.ctrlKey || ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End'].includes(event.key)) {
      return;
    }
    const selectionStart = input.selectionStart ?? input.value.length;
    const selectionEnd = input.selectionEnd ?? input.value.length;
    const prospectiveValue = `${input.value.slice(0, selectionStart)}${event.key}${input.value.slice(selectionEnd)}`;
    if (!this.partialCoordinateRegExp[valueToChange].test(prospectiveValue)) {
      event.preventDefault();
    }
  }

  handleChange(event: any, valueToChange: string) {
    let value = event.target.value;
    if (valueToChange === 'radius') {
      const numericValue = Math.trunc(Number(value));
      if (value === '' || isNaN(numericValue)) {
        event.target.value = this.openedItem.radius;
        return;
      }
      value = String(this.utilsService.clampStopRadius(numericValue));
      event.target.value = value;
    }
    if (valueToChange === 'lng' || valueToChange === 'lat') {
      if (!this.coordinateRegExp[valueToChange].test(value)) {
        value = String(this.utilsService.truncateCoordinate(this.openedItem[valueToChange]));
        event.target.value = value;
      }
    }
    this.onEditSelectedItem.emit({valueToChange, value, id: this.openedItem.id});
  }

  onClickConnection(connectionId: number, type: string) {
    this.onChangeSelectionType.emit(type);
    this.onClickSelectedItem.emit({ids: [connectionId], multiple: false});
  }

  onCloseDetails() {
    this.openedItem = undefined;
    this.onClickSelectedItem.emit({ids: [], multiple: false});
  }

}
